/**
 * Heaper Service endpoints by PromotionLevel
 *
 * The enum is available as an export:
 *
 * ```typescript
 * import { HeaperServiceEndpoint } from '@equipmentshare/heaper'
 * ```
 *
 * @enum
 * @category Environment
 * @hidden
 */
export var HeaperServiceEndpoint;
(function (HeaperServiceEndpoint) {
    HeaperServiceEndpoint["Production"] = "https://reporting-api.equipmentshare.com/heaper";
    HeaperServiceEndpoint["Staging"] = "https://staging-reporting-api.equipmentshare.com/heaper";
    HeaperServiceEndpoint["Sandbox"] = "https://staging-reporting-api.equipmentshare.com/heaper";
})(HeaperServiceEndpoint || (HeaperServiceEndpoint = {}));
/**
 * **Intercom project/app ids by PromotionLevel**
 *
 * This script snippet needs to be in the head with the correct `APP_ID` based on environment.  It is extremely important that this is in the head loaded before your app with the correct `APP_ID`--these values are not sensitive and do not need to be stored as a secret.
 *
 * **Do not boot the app!**
 *
 * `window.Intercom` will automatically be detected and automatically booted with the verified identity hash from the heaper-service backend API response.
 *
 * ```typescript
 * <script>
 * //Set your APP_ID
 * var APP_ID = "APP_ID";

 * (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/' + APP_ID;var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s, x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
 * </script>
 * ```
 *
 * The enum is available as an export:
 * ```typescript
 * import { IntercomProjectId } from '@equipmentshare/heaper'
 * ```
 *
 * @enum
 * @category Environment
 */
export var IntercomProjectId;
(function (IntercomProjectId) {
    /** `Production` environment `Customer Support`: https://app.intercom.com/a/apps/cc3wvy5y */
    IntercomProjectId["Production"] = "cc3wvy5y";
    /** `Staging` environment `Customer Support [TEST]`: https://app.intercom.com/a/apps/n3j6c2wc */
    IntercomProjectId["Staging"] = "n3j6c2wc";
    /** `Sandbox` environment `Customer Support [TEST]`: https://app.intercom.com/a/apps/n3j6c2wc */
    IntercomProjectId["Sandbox"] = "n3j6c2wc";
})(IntercomProjectId || (IntercomProjectId = {}));
/**
 * **Heap T3 Platform (not internal) project/app ids by PromotionLevel**
 *
 * This script snippet needs to be in the head with the correct `APP_ID` based on environment.  It is extremely important that this is in the head loaded before your app with the correct `APP_ID`--these values are not sensitive and do not need to be stored as a secret.
 *
 * ```typescript
 * <script>
 * window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
 *
 * heap.load("APP_ID");
 * </script>
 * ```
 *
 * The enum is available as an export:
 * ```typescript
 * import { HeapProjectId } from '@equipmentshare/heaper'
 * ```
 *
 * @enum
 * @category Environment
 */
export var HeapProjectId;
(function (HeapProjectId) {
    /** `Production` environment for `T3 Platform`: https://heapanalytics.com/app/env/3705153768/overview */
    HeapProjectId[HeapProjectId["Production"] = 3705153768] = "Production";
    /** `Staging` environment for `T3 Platform`: https://heapanalytics.com/app/env/3303332550/overview */
    HeapProjectId[HeapProjectId["Staging"] = 3303332550] = "Staging";
    /** `Sandbox` environment for `T3 Platform`: https://heapanalytics.com/app/env/3798811465/overview */
    HeapProjectId[HeapProjectId["Sandbox"] = 3798811465] = "Sandbox";
})(HeapProjectId || (HeapProjectId = {}));
/**
 * **Heap Internal Products (not T3 Platform) project/app ids by PromotionLevel**
 *
 * This script snippet needs to be in the head with the correct `APP_ID` based on environment.  It is extremely important that this is in the head loaded before your app with the correct `APP_ID`--these values are not sensitive and do not need to be stored as a secret.
 *
 * ```typescript
 * <script>
 * window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
 *
 * heap.load("APP_ID");
 * </script>
 * ```
 *
 * The enum is available as an export:
 * ```typescript
 * import { HeapInternalProjectId } from '@equipmentshare/heaper'
 * ```
 *
 * @enum
 * @category Environment
 */
export var HeapInternalProjectId;
(function (HeapInternalProjectId) {
    /** `Production` environment for `Internal Products`: https://heapanalytics.com/app/env/1146094519/overview */
    HeapInternalProjectId[HeapInternalProjectId["Production"] = 1146094519] = "Production";
    /** `Staging` environment for `Internal Products`: https://heapanalytics.com/app/env/740130035/overview */
    HeapInternalProjectId[HeapInternalProjectId["Staging"] = 740130035] = "Staging";
    /** `Sandbox` environment for `Internal Products`: https://heapanalytics.com/app/env/223070347/overview */
    HeapInternalProjectId[HeapInternalProjectId["Sandbox"] = 223070347] = "Sandbox";
})(HeapInternalProjectId || (HeapInternalProjectId = {}));
//# sourceMappingURL=types.js.map